import { useEffect, useState } from 'react';
import { Elements, ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  loadStripe,
  ExpressCheckoutPaymentMethodsOption,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent
} from '@stripe/stripe-js';
import { createWalletSetupIntent } from '@uplift/api';
import { useStatesQuery } from '@uplift/queries';
import { AddressType } from '@uplift/types';
import { environment } from '../../../environments/environment';

const stripePromise = environment.stripePublishableKey ? loadStripe(environment.stripePublishableKey) : null;

const WALLET_PAYMENT_METHODS: ExpressCheckoutPaymentMethodsOption = {
  applePay: 'auto',
  googlePay: 'auto',
  amazonPay: 'never',
  link: 'never',
  paypal: 'never',
  klarna: 'never'
};

export type WalletPaymentResult = {
  paymentMethodId: string;
  billingAddress: AddressType;
};

type WalletButtonProps = {
  onWalletPayment: (result: WalletPaymentResult) => Promise<void>;
  onAvailabilityChange?: (available: boolean) => void;
};

const WalletExpressCheckout: React.VFC<WalletButtonProps> = ({ onWalletPayment, onAvailabilityChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [ walletAvailable, setWalletAvailable ] = useState(false);
  const { data: states } = useStatesQuery();

  const onReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    const available = !!(event.availablePaymentMethods?.applePay || event.availablePaymentMethods?.googlePay);

    setWalletAvailable(available);
    onAvailabilityChange?.(available);
  };

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      return;
    }

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {},
      redirect: 'if_required'
    });

    if (error || !setupIntent?.payment_method) {
      event.paymentFailed();
      return;
    }

    const billingDetails = event.billingDetails;
    const stateAbbreviation = billingDetails?.address?.state ?? '';
    const stateId = states?.find(state => state.shortName.toUpperCase() === stateAbbreviation.toUpperCase())?.id ?? '';

    await onWalletPayment({
      paymentMethodId: setupIntent.payment_method as string,
      billingAddress: {
        address1: billingDetails?.address?.line1 ?? '',
        address2: billingDetails?.address?.line2 ?? '',
        city: billingDetails?.address?.city ?? '',
        state: stateId,
        zip: billingDetails?.address?.postal_code ?? ''
      }
    });
  };

  return (
    <div style={{ display: walletAvailable ? 'block' : 'none' }}>
      <ExpressCheckoutElement
        options={{
          paymentMethods: WALLET_PAYMENT_METHODS,
          buttonType: { googlePay: 'plain', applePay: 'plain' }
        }}
        onReady={onReady}
        onConfirm={onConfirm}
      />
    </div>
  );
};

export const WalletButton: React.VFC<WalletButtonProps> = props => {
  const { onAvailabilityChange } = props;
  const [ clientSecret, setClientSecret ] = useState<string | null>(null);

  useEffect(() => {
    if (!stripePromise) {
      onAvailabilityChange?.(false);
      return;
    }

    createWalletSetupIntent()
      .then(result => setClientSecret(result.clientSecret))
      .catch(() => onAvailabilityChange?.(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!stripePromise || !clientSecret) {
    return null;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <WalletExpressCheckout {...props} />
    </Elements>
  );
};
